import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { TrendingUp, Loader2, Eye, EyeOff, Check } from 'lucide-react';
import { toast } from 'sonner';

const ResetPassword = () => {
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!token) {
      toast.error('Invalid reset token');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(token, password);
      toast.success('Password reset successfully!');
      navigate('/login');
    } catch (error) {
      const message = error.response?.data?.detail?.message || error.response?.data?.detail || 'Failed to reset password';
      toast.error(typeof message === 'object' ? JSON.stringify(message) : message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4 relative">
      {/* Grid overlay */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `
          linear-gradient(to right, #1F1F1F 1px, transparent 1px),
          linear-gradient(to bottom, #1F1F1F 1px, transparent 1px)
        `,
        backgroundSize: '40px 40px'
      }} />

      <div className="w-full max-w-md relative z-10 animate-fade-in-up">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 bg-[#00FF94] rounded-lg flex items-center justify-center">
            <TrendingUp className="w-6 h-6 text-black" strokeWidth={2.5} />
          </div>
          <span className="font-bold text-2xl text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
            CoinPulse
          </span>
        </Link>

        <Card className="border-[#1F1F1F] bg-[#0A0A0A]">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-bold text-center text-white">Set new password</CardTitle>
            <CardDescription className="text-center text-[#A1A1AA]">
              Enter your new password below
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#EDEDED]">New Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-black border-[#1F1F1F] focus:border-[#00FF94] text-white placeholder:text-[#737373] pr-10"
                    data-testid="reset-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-[#EDEDED]">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-black border-[#1F1F1F] focus:border-[#00FF94] text-white placeholder:text-[#737373]"
                  data-testid="reset-confirm-password-input"
                />
              </div>

              {/* Password requirements */}
              <div className="text-xs text-[#737373] space-y-1">
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${password.length >= 6 ? 'bg-[#00FF94]/20 text-[#00FF94]' : 'bg-[#1F1F1F]'}`}>
                    {password.length >= 6 && <Check className="w-3 h-3" />}
                  </div>
                  <span className={password.length >= 6 ? 'text-[#00FF94]' : ''}>At least 6 characters</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${password === confirmPassword && password.length > 0 ? 'bg-[#00FF94]/20 text-[#00FF94]' : 'bg-[#1F1F1F]'}`}>
                    {password === confirmPassword && password.length > 0 && <Check className="w-3 h-3" />}
                  </div>
                  <span className={password === confirmPassword && password.length > 0 ? 'text-[#00FF94]' : ''}>Passwords match</span>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#00FF94] text-black hover:bg-[#00FF94]/90 font-bold h-11"
                disabled={loading}
                data-testid="reset-password-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
